import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SideBar from '../../components/Topbar';
import { HTMLCourseNavProvider } from "../../components/SidebarCourseContext";
import './assets/styles/HTMLLesson1.css';
import { CopyBlock, dracula } from "react-code-blocks";

// Import sophisticated icons
import { 
  FaCode,
  FaCheck,
  FaArrowRight,
  FaPlay,
  FaBookOpen,
  FaHeading,
  FaSearch,
  FaTextHeight,
  FaMinus,
  FaCog
} from 'react-icons/fa';

const HTMLLesson5 = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [viewedSections, setViewedSections] = useState(new Set([0]));
  const sectionRefs = useRef([]);
  const observerRef = useRef(null);

  const [language] = useState("html");
  const [lineNumbers] = useState(true);

  // Video explanation URL
  const videoUrl = "https://app.codeflarelimited.com/watch-video/html-course";

  const code1 = `<h1>Heading 1</h1>
<h2>Heading 2</h2>
<h3>Heading 3</h3>
<h4>Heading 4</h4>
<h5>Heading 5</h5>
<h6>Heading 6</h6>`;

  const code2 = `<h1 style="font-size: 18px">Heading 1</h1>`;

  const code3 = `<h1>Heading 1</h1>
<p>This is some text...</p>
<hr />
<h2>This is a heading</h2>
<p>This is some other text ..</p>
<hr />`;

  const code4 = `<!DOCTYPE html>
<html>
<head>
<title>My First HTML Project</title>
</head>
<body>

</body>
</html>`;

  const sections = [
    {
      title: "HTML Headings",
      content: "Headings are defined with <h1> to <h6> tags, with <h1> being the most important and <h6> the least important",
      keyPoints: [
        "<h1> defines the most important heading",
        "<h6> defines the least important heading", 
        "Browsers automatically add white space (margin) around headings",
        "Headings create a clear content hierarchy"
      ],
      icon: <FaHeading className="section-icon" />,
      color: "#2563eb",
      code: code1
    },
    {
      title: "Headings Importance", 
      content: "Headings are crucial for both user experience and search engine optimization (SEO)",
      keyPoints: [
        "Search engines use headings to index page structure",
        "Users skim pages by reading headings first",
        "Use <h1> for main titles, then <h2>, <h3> in order",
        "Never use headings just to make text big or bold"
      ],
      icon: <FaSearch className="section-icon" />,
      color: "#1d4ed8",
      code: null
    },
    {
      title: "Custom Heading Sizes",
      content: "While each HTML heading has a default size, you can customize them with CSS",
      keyPoints: [
        "Each heading level has a default browser size",
        "Use CSS font-size property to customize",
        "Style attribute allows inline CSS styling",
        "Maintain proper heading hierarchy when customizing"
      ],
      icon: <FaTextHeight className="section-icon" />,
      color: "#1e40af",
      code: code2
    },
    {
      title: "Horizontal Rules",
      content: "The <hr> tag defines a thematic break and is displayed as a horizontal rule",
      keyPoints: [
        "<hr> creates visual separation between content",
        "Useful for topic changes or section breaks",
        "Self-closing tag (no end tag needed)",
        "Often styled with CSS for different appearances"
      ],
      icon: <FaMinus className="section-icon" />,
      color: "#7c3aed",
      code: code3
    },
    {
      title: "The Head Element",
      content: "The <head> element contains metadata about the HTML document, not visible content",
      keyPoints: [
        "Placed between <html> and <body> tags",
        "Contains metadata like title, character set, and links",
        "Metadata is not displayed on the page",
        "Essential for SEO and proper document setup"
      ],
      icon: <FaCog className="section-icon" />,
      color: "#6b21a8",
      code: code4
    }
  ];

  // Intersection Observer for scroll detection
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-10% 0px -20% 0px',
      threshold: 0.6
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const index = sectionRefs.current.indexOf(entry.target);
        
        if (entry.isIntersecting) {
          setActiveSection(index);
          setViewedSections(prev => new Set([...prev, index]));
        }
      });
    }, options);

    sectionRefs.current.forEach((section) => {
      if (section) {
        observerRef.current.observe(section);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const addSectionRef = (element, index) => {
    sectionRefs.current[index] = element;
  };

  const getCompletionPercentage = () => {
    const totalSections = sections.length + 2;
    const viewedCount = viewedSections.size;
    return Math.round((viewedCount / totalSections) * 100);
  };

  return (
    <HTMLCourseNavProvider>
      <div className="html-lesson-container">
        <SideBar />
        
        <div className="html-lesson-main">
          {/* Header Section */}
          <div className="html-lesson-header">
            <div className="html-header-content">
              <div className="html-lesson-badge">
                <FaBookOpen className="html-badge-icon" />
                Lesson 5 • {getCompletionPercentage()}% Viewed
              </div>
              <h1 className="html-lesson-title">HTML Headings & Document Structure</h1>
              <p className="html-lesson-subtitle">
                Master heading hierarchy, document structure, and metadata for better SEO and user experience
              </p>
              
              {/* Progress Bar */}
              <div className="html-completion-progress">
                <div 
                  className="html-completion-fill"
                  style={{ width: `${getCompletionPercentage()}%` }}
                ></div>
                <div className="html-progress-labels">
                  <span>Getting Started</span>
                  <span>Complete</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="html-progress-indicator">
            {sections.map((section, index) => (
              <div 
                key={index}
                className={`html-progress-step ${viewedSections.has(index) ? 'html-viewed' : ''} ${index === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-step-number">
                  {viewedSections.has(index) ? '✓' : index + 1}
                </div>
                <div className="html-step-label">{section.title}</div>
                {index < sections.length - 1 && <div className="html-step-connector"></div>}
              </div>
            ))}
          </div>

          {/* Visual Heading Demonstration */}
          <div 
            className="html-visual-headings"
            ref={(el) => addSectionRef(el, 0)}
          >
            <div className="html-visual-header">
              <div className="html-code-badge">
                <FaHeading className="html-badge-icon" />
                Heading Hierarchy
              </div>
              <h3 className="html-visual-title">
                See All Heading Levels in Action
              </h3>
            </div>
            
           
            <div className="html-headings-display ">
              <h1 style={{ margin: '1rem 0', color: '#1e293b' }}>Heading 1 - Most Important</h1>
              <h2 style={{ margin: '0.875rem 0', color: '#374151', textAlign: 'left' }}>Heading 2 - Section Title</h2>
              <h3 style={{ margin: '0.75rem 0', color: '#4b5563' }}>Heading 3 - Subsection</h3>
              <h4 style={{ margin: '0.625rem 0', color: '#6b7280' }}>Heading 4 - Minor Section</h4>
              <h5 style={{ margin: '0.5rem 0', color: '#9ca3af' }}>Heading 5 - Small Heading</h5>
              <h6 style={{ margin: '0.375rem 0', color: '#d1d5db' }}>Heading 6 - Least Important</h6>
            </div>
             
            
            <div className="html-visual-note">
              <p><strong>Note:</strong> Headings create a visual hierarchy. Use them in order (h1 → h2 → h3) for proper document structure.</p>
            </div>
          </div>

          {/* Content Sections */}
          <div className="html-content-sections">
            {sections.map((section, index) => (
              <div 
                key={index}
                ref={(el) => addSectionRef(el, index + 1)}
                className={`html-content-card ${viewedSections.has(index + 1) ? 'html-viewed' : ''} ${(index + 1) === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-card-badge">
                  {viewedSections.has(index + 1) ? '✓ Viewed' : `Part ${index + 1}`}
                </div>
                
                <div className="html-card-header">
                  <div className="html-card-icon" style={{ backgroundColor: section.color }}>
                    {section.icon}
                  </div>
                  <div className="html-card-text">
                    <h2 className="html-card-title">{section.title}</h2>
                    <p className="html-card-content" dangerouslySetInnerHTML={{ __html: section.content }} />
                    
                    <div className="html-key-points">
                      <h4>Key Points:</h4>
                      <ul>
                        {section.keyPoints.map((point, idx) => (
                          <li key={idx} dangerouslySetInnerHTML={{ __html: point }} />
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Code Example for sections that have code */}
                {section.code && (
                  <div className="html-section-code">
                    <div className="html-code-badge">
                      <FaCode className="html-badge-icon" />
                      Example
                    </div>
                    <div className="html-code-block-small">
                      <CopyBlock
                        language={language}
                        text={section.code}
                        showLineNumbers={lineNumbers}
                        theme={dracula}
                        wrapLines={true}
                        codeBlock
                      />
                    </div>
                  </div>
                )}
                
                <div className="html-card-actions">
                  <a 
                    href={videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="html-action-btn"
                  >
                    <FaPlay className="html-btn-icon" />
                    Watch Video Explanation
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Best Practices Section */}
          <div 
            className="html-practices-section"
            ref={(el) => addSectionRef(el, sections.length + 1)}
          >
            <div className="html-practices-header">
              <div className="html-code-badge">
                <FaCheck className="html-badge-icon" />
                Best Practices
              </div>
              <h3 className="html-practices-title">
                Heading & Structure Guidelines
              </h3>
            </div>
            
            <div className="html-practices-grid">
              <div className="html-practice-card html-do">
                <h4>✅ Do This</h4>
                <ul>
                  <li>Use only one <code>&lt;h1&gt;</code> per page</li>
                  <li>Maintain proper heading hierarchy</li>
                  <li>Use headings for actual section titles</li>
                  <li>Make headings descriptive and concise</li>
                  <li>Use CSS to style headings when needed</li>
                </ul>
              </div>
              
              <div className="html-practice-card html-dont">
                <h4>❌ Avoid This</h4>
                <ul>
                  <li>Don't skip heading levels (h1 → h3)</li>
                  <li>Don't use headings for visual effects only</li>
                  <li>Avoid multiple <code>&lt;h1&gt;</code> headings</li>
                  <li>Don't use headings out of hierarchy order</li>
                  <li>Avoid very long heading text</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Comprehensive Example */}
          <div 
            className="html-code-example"
            ref={(el) => addSectionRef(el, sections.length + 2)}
          >
            <div className="html-code-header">
              <div className="html-code-badge">
                <FaCode className="html-badge-icon" />
                Complete Example
              </div>
              <h3 className="html-code-title">
                Proper Document Structure
              </h3>
            </div>
            <div className="html-code-block">
              <CopyBlock
                language={language}
                text={`<!DOCTYPE html>
<html>
<head>
    <title>My Webpage - HTML Headings Guide</title>
    <meta charset="UTF-8">
    <meta name="description" content="Learn about HTML headings">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body>
    <!-- Main page heading -->
    <h1>HTML Headings Complete Guide</h1>
    
    <!-- Introduction section -->
    <p>Welcome to our comprehensive guide on HTML headings and document structure.</p>
    
    <hr>
    
    <!-- Section 1 -->
    <h2>Understanding Heading Hierarchy</h2>
    <p>Headings create the structure of your document...</p>
    
    <!-- Subsection -->
    <h3>Heading Levels Explained</h3>
    <p>There are six heading levels available...</p>
    
    <!-- Sub-subsection -->
    <h4>When to Use Each Level</h4>
    <p>Choose heading levels based on content importance...</p>
    
    <hr>
    
    <!-- Section 2 -->
    <h2>SEO Best Practices</h2>
    <p>Search engines use headings to understand content...</p>
    
    <!-- Subsection -->
    <h3>Keyword Placement</h3>
    <p>Include relevant keywords in your headings...</p>
    
    <hr>
    
    <!-- Section 3 -->
    <h2>Accessibility Considerations</h2>
    <p>Proper heading structure helps screen readers...</p>
    
    <footer>
        <p>&copy; 2024 My Website. All rights reserved.</p>
    </footer>
</body>
</html>`}
                showLineNumbers={lineNumbers}
                theme={dracula}
                wrapLines={true}
                codeBlock
              />
            </div>
            <div className="html-code-actions">
              <a 
                href={videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="html-code-btn html-primary"
              >
                Watch Complete Implementation Guide
              </a>
            </div>
          </div>

          {/* Action Section */}
          <div 
            className="html-action-section"
            ref={(el) => addSectionRef(el, sections.length + 3)}
          >
            <div className="html-action-header">
              <h3>Mastered HTML headings and structure?</h3>
              <div className="html-completion-badge">
                {getCompletionPercentage()}% of content viewed
              </div>
            </div>
            
            <div className="html-action-content">
              <p>You've learned about heading hierarchy, document structure, and best practices. Start implementing proper heading structure in your projects!</p>
              
              <div className="html-action-buttons">
                <button 
                  className={`html-complete-button ${isCompleted ? 'html-completed' : ''}`}
                  onClick={() => setIsCompleted(true)}
                >
                  {isCompleted ? (
                    <>
                      <FaCheck className="html-button-icon" />
                      Lesson Completed
                    </>
                  ) : (
                    'Mark as Complete'
                  )}
                </button>
                
                {isCompleted && (
                  <Link to="/html-view-source-code" className="html-next-lesson-link">
                    Continue to Next Lesson
                    <FaArrowRight className="html-link-icon" />
                  </Link>
                )}
              </div>
              
              <div className="html-quick-nav">
                <span>Need more guidance? </span>
                <a 
                  href={videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="html-nav-link"
                >
                  Watch Complete HTML Course
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HTMLCourseNavProvider>
  );
}

export default HTMLLesson5;